import React, { useState, useEffect, useCallback, useRef } from 'react';
import { S } from '../styles';
import { getTheme } from '../theme';
import { API, authHeaders } from '../utils/helpers';

function formatReceiptNote(ticket, template, items, generalNote, techName) {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' });
  const lines = [];
  lines.push(`${template.name} — ${dateStr}`);
  lines.push(`Tech: ${techName || ticket.tech || 'Unknown'}`);
  lines.push('');
  items.forEach(item => {
    const icon = item.status === 'pass' ? '✓' : item.status === 'attention' ? '⚠' : '○';
    lines.push(`${icon} ${item.label}`);
    if (item.note?.trim()) lines.push(`   Note: ${item.note.trim()}`);
  });
  if (generalNote?.trim()) {
    lines.push('');
    lines.push(`Tech Notes: ${generalNote.trim()}`);
  }
  return lines.join('\n');
}

function PreviewPanel({ receiptNote, T, isDark }) {
  return (
    <div style={{ width:300, flexShrink:0, position:'sticky', top:0, alignSelf:'flex-start',
      background: isDark ? '#1e2a40' : '#f8f8f4',
      border: `1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
      borderRadius:10, overflow:'hidden' }}>
      <div style={{ padding:'12px 16px', background: isDark ? '#243050' : '#eeeee8',
        borderBottom: `1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}` }}>
        <div style={{ fontSize:11, fontWeight:700, color: isDark ? '#5a8ab8' : '#666',
          textTransform:'uppercase', letterSpacing:'0.08em' }}>Live Preview</div>
        <div style={{ fontSize:11, color: isDark ? '#5a6a8a' : '#999', marginTop:2 }}>
          What will be written to Lightspeed
        </div>
      </div>
      <div style={{ padding:'14px 16px', fontSize:12, fontFamily:"'IBM Plex Mono', monospace",
        color: isDark ? '#ccd0dc' : '#333', lineHeight:1.8, whiteSpace:'pre-wrap',
        wordBreak:'break-word', maxHeight:'calc(100vh - 200px)', overflowY:'auto' }}>
        {receiptNote || <span style={{ color: isDark ? '#3a4a6a' : '#bbb', fontStyle:'italic' }}>
          Start reviewing items to see preview…</span>}
      </div>
    </div>
  );
}

export default function Checklist({ ticket, template, templates = [], statuses, theme, onClose, onSave, readOnly = false, autoDetected = false, onSwitchTemplate, techFinalizationStatuses = [] }) {
  const T = getTheme(theme);
  const isDark = theme === 'dark';
  const [showAutoDetectToast, setShowAutoDetectToast] = useState(autoDetected);

  useEffect(() => {
    if (!showAutoDetectToast) return;
    const t = setTimeout(() => setShowAutoDetectToast(false), 3000);
    return () => clearTimeout(t);
  }, [showAutoDetectToast]);

  const [items, setItems] = useState([]);
  const [generalNote, setGeneralNote] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [selectedStatusId, setSelectedStatusId] = useState('');
  const [writeSuccess, setWriteSuccess] = useState(false);
  const [writeError, setWriteError] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemLabel, setNewItemLabel] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [templateMismatch, setTemplateMismatch] = useState(false);
  const [showSwitchTemplate, setShowSwitchTemplate] = useState(false);
  const initialized = useRef(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const initFromTemplate = useCallback(() => {
    setItems(template.items.map(item => ({
      id: `item_${Date.now()}_${Math.random()}`,
      label: item.label, status: 'pending', note: '', custom: item.custom || false,
    })));
    setGeneralNote(''); setExpandedItem(null); setShowSubmit(false); setTemplateMismatch(false);
  }, [template]);

  useEffect(() => {
    const loadProgress = async () => {
      try {
        const res = await fetch(`${API}/api/checklists/${ticket.id}`, { credentials:'include', headers: authHeaders() });
        const data = await res.json();
        if (data?.items?.length) {
          // Skip mismatch check if: read-only mode OR checklist was already submitted
          if (!readOnly && !data.submitted && data.templateId && data.templateId !== template.id) {
            setTemplateMismatch(true); return;
          }
          setItems(data.items); setGeneralNote(data.generalNote || ''); return;
        }
      } catch (_) {}
      initFromTemplate();
    };
    loadProgress();
  }, [ticket.id, template.id, readOnly]);

  const autoSave = useCallback(async (currentItems, currentNote) => {
    if (readOnly) return;
    try {
      const data = { ticketId:ticket.id, templateId:template.id, templateName:template.name,
        items:currentItems, generalNote:currentNote, tech:ticket.tech };
      await fetch(`${API}/api/checklists/${ticket.id}`, {
        method:'POST', credentials:'include',
        headers: authHeaders(true), body:JSON.stringify(data),
      });
      if (onSave) onSave(data);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (_) {}
  }, [ticket.id, template, onSave, readOnly]);

  const setItemStatus = (id, status) => {
    const updated = items.map(i => i.id === id ? { ...i, status } : i);
    setItems(updated); autoSave(updated, generalNote);
  };
  const setItemNote = (id, note) => {
    const updated = items.map(i => i.id === id ? { ...i, note } : i);
    setItems(updated); autoSave(updated, generalNote);
  };
  const setNote = (note) => { setGeneralNote(note); autoSave(items, note); };

  const addCustomItem = () => {
    if (!newItemLabel.trim()) return;
    const updated = [...items, { id:`item_${Date.now()}`, label:newItemLabel.trim(), status:'pending', note:'', custom:true }];
    setItems(updated); setNewItemLabel(''); setShowAddItem(false); autoSave(updated, generalNote);
  };
  const removeItem = (id) => {
    const updated = items.filter(i => i.id !== id);
    setItems(updated); autoSave(updated, generalNote);
  };
  const handleReset = async () => {
    // Return the ticket to its "not started" state EVERYWHERE:
    // - Clear the Lightspeed receipt note (which Submit wrote)
    // - DELETE the checklist record (Tech Report goes back to "Start Checklist")
    // - DELETE the completion record if present (Service Queue "Checklist done" badge clears)
    // Lightspeed status is intentionally NOT reverted — status changes are workflow decisions.
    try {
      await Promise.all([
        fetch(`${API}/api/tickets/${ticket.id}/note`, {
          method:'PUT', credentials:'include',
          headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`}, body:JSON.stringify({ note:'' }),
        }),
        fetch(`${API}/api/checklists/${ticket.id}`, { method:'DELETE', credentials:'include', headers: authHeaders() }),
        fetch(`${API}/api/completed-checklists/${ticket.id}`, { method:'DELETE', credentials:'include', headers: authHeaders() }),
      ]);
    } catch (_) {}
    // Re-init local state so the user sees a fresh checklist if they want to keep working.
    initFromTemplate();
    setShowResetConfirm(false);
  };

  const doneCount = items.filter(i => i.status === 'pass' || i.status === 'attention').length;
  const attentionWithoutNote = items.filter(i => i.status === 'attention' && !i.note?.trim());
  const allDone = doneCount === items.length && items.length > 0;
  const generalNoteRequired = !generalNote.trim();
  const canSubmit = allDone && attentionWithoutNote.length === 0 && !generalNoteRequired;

  const getBlockingReasons = () => {
    const r = [];
    const p = items.filter(i => i.status === 'pending').length;
    if (p > 0) r.push(`${p} item${p > 1 ? 's' : ''} not reviewed`);
    if (attentionWithoutNote.length > 0) r.push(`${attentionWithoutNote.length} "Needs Attention" item${attentionWithoutNote.length > 1 ? 's' : ''} missing a note`);
    if (generalNoteRequired) r.push('Tech Notes is required');
    return r;
  };

  const receiptNote = formatReceiptNote(ticket, template, items, generalNote, ticket.tech);

  const handleSubmit = async () => {
    setSubmitting(true); setWriteError(null);
    try {
      const noteRes = await fetch(`${API}/api/tickets/${ticket.id}/note`, {
        method:'PUT', credentials:'include',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`}, body:JSON.stringify({ note:receiptNote }),
      });
      if (!noteRes.ok) throw new Error('Failed to write note');
      if (selectedStatusId) {
        await fetch(`${API}/api/tickets/${ticket.id}/status`, {
          method:'PUT', credentials:'include',
          headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`},
          body:JSON.stringify({ workorderStatusID:selectedStatusId, clearCompletion:false }),
        });
      }
      await fetch(`${API}/api/checklists/${ticket.id}`, {
        method:'POST', credentials:'include',
        headers: authHeaders(true),
        body:JSON.stringify({ ticketId:ticket.id, templateId:template.id, templateName:template.name,
          items, generalNote, tech:ticket.tech, submitted:true }),
      });
      await fetch(`${API}/api/completed-checklists/${ticket.id}`, {
        method:'POST', credentials:'include',
        headers: authHeaders(true),
        body:JSON.stringify({ ticketId:ticket.id, templateName:template.name, tech:ticket.tech,
          itemCount:items.length, attentionCount:items.filter(i => i.status === 'attention').length }),
      });
      if (onSave) onSave({ ticketId:ticket.id, templateId:template.id, templateName:template.name,
        items, generalNote, tech:ticket.tech, submitted:true });
      setWriteSuccess(true);
      setTimeout(() => onClose(), 2000);
    } catch (err) { setWriteError(err.message || 'Write failed'); }
    setSubmitting(false);
  };

  // ── Template mismatch prompt ──────────────────────────────────────────────
  if (templateMismatch) {
    return (
      <div style={{ maxWidth:500, margin:'80px auto', background:T.card,
        border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`, borderRadius:12,
        padding:'32px 36px', textAlign:'center' }}>
        <div style={{ fontSize:32, marginBottom:16 }}>⚠️</div>
        <div style={{ fontSize:17, fontWeight:700, color:T.text, marginBottom:10 }}>Different template detected</div>
        <div style={{ fontSize:13, color:T.textSub, marginBottom:24, lineHeight:1.6 }}>
          This ticket has a saved checklist from a different template. Start fresh with{' '}
          <strong style={{ color:T.text }}>{template.name}</strong>, or go back?
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
          <button onClick={initFromTemplate}
            style={{ ...S.btn(true), background:'#F4622A', color:'#fff', padding:'10px 24px', fontSize:13 }}>
            Start fresh with {template.name}
          </button>
          <button onClick={onClose}
            style={{ ...S.btn(false), color:T.textSub, borderColor:T.border, padding:'10px 20px', fontSize:13 }}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Auto-detect toast */}
      {showAutoDetectToast && (
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 16px', marginBottom:12,
          background: isDark ? '#0f2a15' : '#d4edda',
          border:`1px solid #2ecc71`, borderRadius:8,
          animation:'fadeIn 0.3s ease' }}>
          <span style={{ fontSize:16 }}>✓</span>
          <span style={{ fontSize:13, color:'#2ecc71', fontWeight:500 }}>
            Template auto-detected: <strong>{template.name}</strong>
          </span>
          <button onClick={() => setShowAutoDetectToast(false)}
            style={{ marginLeft:'auto', background:'transparent', border:'none', color:'#2ecc71', cursor:'pointer', fontSize:16 }}>×</button>
        </div>
      )}

      {/* Ticket info bar — prominent, always visible */}
      <div style={{ background: isDark ? '#1e2a40' : '#f4f4ef',
        border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
        borderRadius:10, padding:'14px 18px', marginBottom:16,
        borderLeft:`4px solid ${readOnly ? '#3498db' : '#F4622A'}` }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
              <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:12,
                color: isDark ? '#5a8ab8' : '#888', fontWeight:600 }}>{ticket.displayId}</span>
              <span style={S.statusPill(ticket.status)}>{ticket.status}</span>
              {readOnly && (
                <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4,
                  background: isDark ? '#1a2a3a' : '#e8f0f8', color:'#3498db',
                  fontWeight:500, border:`1px solid #3498db44` }}>Read-only</span>
              )}
            </div>
            <div style={{ fontSize:17, fontWeight:700, color:T.text, marginBottom:3 }}>{ticket.customer}</div>
            <div style={{ fontSize:14, color:T.textSub }}>{ticket.bike}</div>
          </div>
          <div style={{ textAlign:'right', flexShrink:0 }}>
            <div style={{ fontSize:11, color:T.textSub, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.06em' }}>Template</div>
            <div style={{ fontSize:16, fontWeight:700, color: readOnly ? '#3498db' : T.text }}>{template.name}</div>
            {!readOnly && templates.length > 1 && (
              <button onClick={() => setShowSwitchTemplate(v => !v)}
                style={{ fontSize:12, color:'#3498db', background:'transparent', border:`1px solid #3498db44`,
                  borderRadius:5, cursor:'pointer', padding:'3px 10px', marginTop:6,
                  fontWeight:500 }}>
                {showSwitchTemplate ? '× Cancel' : '⇄ Switch template'}
              </button>
            )}
            {showSwitchTemplate && (
              <div style={{ marginTop:8, minWidth:220 }}>
                <select onChange={e => {
                    if (!e.target.value) return;
                    setShowSwitchTemplate(false);
                    if (onSwitchTemplate) onSwitchTemplate(e.target.value);
                    e.target.value = '';
                  }} defaultValue=""
                  style={{ background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6,
                    padding:'7px 10px', color:T.text, fontSize:13, cursor:'pointer', outline:'none', width:'100%' }}>
                  <option value="">— Select template —</option>
                  {templates.filter(tp => tp.id !== template.id).map(tp => (
                    <option key={tp.id} value={tp.id}>{tp.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Header row — back button + controls */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <button onClick={onClose} style={{ background:'transparent', border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
          borderRadius:6, color:T.textSub, cursor:'pointer', fontSize:12, padding:'5px 12px',
          display:'flex', alignItems:'center', gap:6 }}>
          ← Back to Tech View
        </button>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {saved && !readOnly && <span style={{ fontSize:12, color:'#2ecc71' }}>✓ Saved</span>}
          {!readOnly && (showResetConfirm ? (
            <div style={{ display:'flex', alignItems:'center', gap:10,
              background: isDark ? '#2a0f0f' : '#fde8e8',
              border:'1px solid #e74c3c', borderRadius:8, padding:'8px 14px' }}>
              <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                <span style={{ fontSize:12, color:'#e74c3c', fontWeight:500 }}>Reset all progress?</span>
                <span style={{ fontSize:10, color: isDark ? '#c97070' : '#a85050', opacity:0.85 }}>
                  Also clears the Lightspeed receipt note
                </span>
              </div>
              <button onClick={handleReset} style={{ fontSize:11, padding:'3px 10px', borderRadius:5,
                background:'#e74c3c', border:'none', color:'#fff', cursor:'pointer' }}>Yes, reset</button>
              <button onClick={() => setShowResetConfirm(false)} style={{ fontSize:11, padding:'3px 10px',
                borderRadius:5, background:'transparent',
                border:`1px solid ${isDark ? '#3a4a6a' : '#ccc'}`, color:T.textSub, cursor:'pointer' }}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => setShowResetConfirm(true)}
              style={{ ...S.btn(false), fontSize:11, padding:'5px 12px', color:'#e74c3c', borderColor:'#e74c3c33' }}>
              ↺ Reset
            </button>
          ))}
          <button onClick={() => setShowPreview(v => !v)}
            style={{ ...S.btn(false), fontSize:11, padding:'5px 12px',
              color: showPreview ? '#3498db' : T.textSub,
              borderColor: showPreview ? '#3498db' : T.border }}>
            {showPreview ? '▶ Hide Preview' : '▶ Show Preview'}
          </button>
          <div style={{ textAlign:'right', minWidth:48 }}>
            <div style={{ fontSize:22, fontWeight:700, color: allDone ? '#2ecc71' : '#F4622A',
              fontFamily:'Arial, sans-serif' }}>{doneCount}/{items.length}</div>
            <div style={{ fontSize:11, color:T.textSub }}>done</div>
          </div>
        </div>
      </div>

      {/* Sticky progress bar */}
      <div style={{ position:'sticky', top:0, zIndex:10, background:T.bg, paddingBottom:12, marginBottom:8 }}>
        <div style={{ height:6, borderRadius:3, background: isDark ? '#243050' : '#e0e0d8', overflow:'hidden' }}>
          <div style={{ height:'100%', borderRadius:3,
            width:`${items.length ? (doneCount/items.length)*100 : 0}%`,
            background: allDone ? '#2ecc71' : '#F4622A', transition:'width 0.3s' }} />
        </div>
      </div>

      {/* Split layout */}
      <div style={{ display:'flex', gap:20, alignItems:'flex-start' }}>
        <div style={{ flex:1, minWidth:0 }}>

          {/* Checklist items */}
          <div style={{ display:'grid', gap:8, marginBottom:20 }}>
            {items.map(item => (
              <div key={item.id} style={{
                background:T.card,
                border:`1px solid ${!readOnly && item.status === 'attention' && !item.note?.trim() ? '#e74c3c' : item.status === 'pass' ? '#2ecc7133' : item.status === 'attention' ? '#e67e2233' : T.cardBorder}`,
                borderRadius:10, overflow:'hidden',
              }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px' }}>
                  {/* Status indicators */}
                  <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                    <div onClick={() => !readOnly && setItemStatus(item.id, item.status === 'pass' ? 'pending' : 'pass')}
                      style={{ width:34, height:34, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center',
                        border:`2px solid ${item.status === 'pass' ? '#2ecc71' : isDark ? '#2e3d58' : '#d0d0c8'}`,
                        background: item.status === 'pass' ? '#2ecc7122' : 'transparent',
                        color: item.status === 'pass' ? '#2ecc71' : T.textSub,
                        fontSize:16, cursor: readOnly ? 'default' : 'pointer' }}>✓</div>
                    <div onClick={() => {
                        if (readOnly) return;
                        const ns = item.status === 'attention' ? 'pending' : 'attention';
                        setItemStatus(item.id, ns);
                        if (ns === 'attention') setExpandedItem(item.id);
                      }}
                      style={{ width:34, height:34, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center',
                        border:`2px solid ${item.status === 'attention' ? '#e67e22' : isDark ? '#2e3d58' : '#d0d0c8'}`,
                        background: item.status === 'attention' ? '#e67e2222' : 'transparent',
                        color: item.status === 'attention' ? '#e67e22' : T.textSub,
                        fontSize:16, cursor: readOnly ? 'default' : 'pointer' }}>⚠</div>
                  </div>

                  {/* Label */}
                  <div style={{ flex:1, fontSize:14,
                    color: item.status === 'pending' ? T.textSub : T.text,
                    fontWeight: item.status !== 'pending' ? 500 : 400 }}>
                    {item.label}
                    {item.custom && <span style={{ fontSize:10, color:T.textMuted, marginLeft:6 }}>custom</span>}
                  </div>

                  {/* Note controls */}
                  <div style={{ display:'flex', gap:6, flexShrink:0, alignItems:'center' }}>
                    {!readOnly && item.status === 'attention' && !item.note?.trim() && (
                      <span style={{ fontSize:10, color:'#e74c3c', fontWeight:500 }}>note required</span>
                    )}
                    {item.note && (
                      <button onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                        style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #3498db',
                          background:'#3498db22', color:'#3498db', cursor:'pointer', fontSize:11 }}>
                        {expandedItem === item.id ? 'Hide note' : '📝 Note'}
                      </button>
                    )}
                    {!readOnly && !item.note && (
                      <button onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                        style={{ padding:'4px 10px', borderRadius:6,
                          border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
                          background:'transparent', color:T.textSub, cursor:'pointer', fontSize:11 }}>
                        + Note
                      </button>
                    )}
                    {!readOnly && item.custom && (
                      <button onClick={() => removeItem(item.id)}
                        style={{ padding:'4px 8px', borderRadius:6,
                          border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
                          background:'transparent', color:'#e74c3c', cursor:'pointer', fontSize:11 }}>×</button>
                    )}
                  </div>
                </div>

                {/* Note field */}
                {expandedItem === item.id && (
                  <div style={{ padding:'0 16px 14px' }}>
                    {readOnly ? (
                      <div style={{ background: isDark ? '#243050' : '#f4f4ef',
                        border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
                        borderRadius:6, padding:'8px 10px', color:T.textSub, fontSize:12,
                        fontFamily:'Arial, sans-serif', lineHeight:1.5 }}>{item.note}</div>
                    ) : (
                      <textarea autoFocus value={item.note}
                        onChange={e => setItemNote(item.id, e.target.value)}
                        placeholder="Add a note about this item…" rows={2}
                        style={{ width:'100%', background: isDark ? '#243050' : '#f4f4ef',
                          border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
                          borderRadius:6, padding:'8px 10px', color:T.text, fontSize:12,
                          fontFamily:'Arial, sans-serif', resize:'vertical', outline:'none', boxSizing:'border-box' }} />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add custom item — edit only */}
          {!readOnly && (showAddItem ? (
            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
              <input autoFocus value={newItemLabel} onChange={e => setNewItemLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addCustomItem(); if (e.key === 'Escape') setShowAddItem(false); }}
                placeholder="Enter checklist item…"
                style={{ flex:1, background:T.input, border:`1px solid ${T.inputBorder}`,
                  borderRadius:8, padding:'8px 12px', color:T.text, fontSize:13,
                  fontFamily:'Arial, sans-serif', outline:'none' }} />
              <button onClick={addCustomItem} style={{ ...S.btn(true), background:'#F4622A', color:'#fff', padding:'8px 16px' }}>Add</button>
              <button onClick={() => setShowAddItem(false)} style={{ ...S.btn(false), padding:'8px 16px', color:T.textSub, borderColor:T.border }}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => setShowAddItem(true)}
              style={{ ...S.btn(false), marginBottom:20, color:T.textSub, borderColor:T.border, fontSize:12 }}>
              + Add custom item
            </button>
          ))}

          {/* Tech Notes */}
          <div style={{ marginBottom:24 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
              <div style={{ fontSize:12, fontWeight:600, color:T.textSub,
                textTransform:'uppercase', letterSpacing:'0.06em' }}>Tech Notes</div>
              {!readOnly && <span style={{ fontSize:10, color:'#e74c3c', fontWeight:500 }}>required</span>}
              {!readOnly && generalNote.trim() && <span style={{ fontSize:10, color:'#2ecc71', marginLeft:'auto' }}>✓</span>}
            </div>
            {readOnly ? (
              <div style={{ background: isDark ? '#243050' : '#f4f4ef',
                border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
                borderRadius:8, padding:'10px 12px', color:T.textSub, fontSize:13,
                fontFamily:'Arial, sans-serif', lineHeight:1.6, whiteSpace:'pre-wrap', minHeight:60 }}>
                {generalNote || <span style={{ fontStyle:'italic', opacity:0.5 }}>No tech notes recorded</span>}
              </div>
            ) : (
              <textarea value={generalNote} onChange={e => setNote(e.target.value)}
                placeholder="Required — add notes for the customer receipt…" rows={3}
                style={{ width:'100%', background:T.input,
                  border:`1px solid ${generalNote.trim() ? '#2ecc71' : '#e74c3c'}`,
                  borderRadius:8, padding:'10px 12px', color:T.text, fontSize:13,
                  fontFamily:'Arial, sans-serif', resize:'vertical', outline:'none', boxSizing:'border-box' }} />
            )}
          </div>

          {/* Blocking reasons + submit — edit mode only */}
          {!readOnly && (
            <div>
              {!canSubmit && getBlockingReasons().length > 0 && (
                <div style={{ background: isDark ? '#1e2a40' : '#f4f4ef',
                  border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
                  borderRadius:8, padding:'12px 14px', marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'#e74c3c', marginBottom:6 }}>Before you can submit:</div>
                  {getBlockingReasons().map((r, i) => (
                    <div key={i} style={{ fontSize:12, color:T.textSub, display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                      <span style={{ color:'#e74c3c' }}>•</span> {r}
                    </div>
                  ))}
                </div>
              )}

              {!showSubmit ? (
                <button onClick={() => canSubmit && setShowSubmit(true)} disabled={!canSubmit}
                  style={{ ...S.btn(true),
                    background: canSubmit ? '#F4622A' : isDark ? '#243050' : '#d0d0c8',
                    color: canSubmit ? '#fff' : T.textSub,
                    cursor: canSubmit ? 'pointer' : 'not-allowed', fontSize:14, padding:'12px 28px' }}>
                  {canSubmit ? 'Submit & Write to Lightspeed →' : 'Complete all requirements to submit'}
                </button>
              ) : writeSuccess ? (
                <div style={{ background: isDark ? '#0f2a15' : '#d4edda', border:'1px solid #2ecc71', borderRadius:10,
                  padding:'16px 20px', color: isDark ? '#2ecc71' : '#1a7a3a', fontSize:14, fontWeight:500 }}>
                  ✓ Receipt note written to Lightspeed successfully. Closing…
                </div>
              ) : (
                <div style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:'20px' }}>
                  <div style={{ fontSize:14, fontWeight:700, color:T.text, marginBottom:16 }}>Review & Submit</div>
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:12, fontWeight:600, color:T.textSub,
                      textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>
                      Update Ticket Status (optional)
                    </div>
                    <select value={selectedStatusId} onChange={e => setSelectedStatusId(e.target.value)}
                      style={{ background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:8,
                        padding:'8px 12px', color:T.text, fontSize:13, fontFamily:'Arial, sans-serif',
                        outline:'none', width:'100%', cursor:'pointer' }}>
                      <option value="">— Keep current status ({ticket.status}) —</option>
                      {statuses.filter(s => {
  const lower = s.name.toLowerCase();
  if ((lower.includes('done') && lower.includes('paid')) || lower === 'invoiced') return false;
  if (techFinalizationStatuses.length > 0) return techFinalizationStatuses.includes(lower);
  return true;
}).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  {writeError && (
                    <div style={{ background: isDark ? '#2a0f0f' : '#fde8e8', border:'1px solid #e74c3c', borderRadius:8,
                      padding:'10px 14px', color:'#e74c3c', fontSize:12, marginBottom:12 }}>
                      Error: {writeError}
                    </div>
                  )}
                  <div style={{ display:'flex', gap:10 }}>
                    <button onClick={handleSubmit} disabled={submitting}
                      style={{ ...S.btn(true), background:'#F4622A', color:'#fff', fontSize:13,
                        padding:'10px 24px', opacity: submitting ? 0.7 : 1 }}>
                      {submitting ? 'Writing to Lightspeed…' : 'Confirm & Submit'}
                    </button>
                    <button onClick={() => setShowSubmit(false)}
                      style={{ ...S.btn(false), color:T.textSub, borderColor:T.border }}>Back</button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Live preview panel */}
        {showPreview && <PreviewPanel receiptNote={receiptNote} T={T} isDark={isDark} />}
      </div>
    </div>
  );
}
